import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X } from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TERMS = `1. The above prices are FOR delivery at specified location.
2. Taxes: GST extra as applicable on individual items.
3. Payment: 50% advance, balance against delivery.
4. Delivery: Within 2 weeks from the date of confirmed order.
5. Our offer is valid for 30 days from the date of this quotation.
6. Goods once sold will not be taken back or exchanged.
7. Our GST No.: `;

function buildDefaultTerms(gstNumber) {
  return `1. The above prices are FOR delivery at specified location.
2. Taxes: GST extra as applicable on individual items.
3. Payment: 50% advance, balance against delivery.
4. Delivery: Within 2 weeks from the date of confirmed order.
5. Our offer is valid for 30 days from the date of this quotation.
6. Goods once sold will not be taken back or exchanged.
7. Our GST No.: ${gstNumber || ''}`;
}

let _formKeyCounter = 0;
const genKey = () => `fk-${++_formKeyCounter}-${Math.random().toString(36).slice(2, 6)}`;

function newItem() {
  return {
    _key: genKey(), _featureKeys: [],
    product_id: '', product_name: '', description: '', features: [], warranty: '',
    hsn_code: '', quantity: 1, unit: 'Nos.', price: 0, gst_rate: 0, item_total: 0,
  };
}

function newBuybackItem() {
  return { _key: genKey(), product_name: '', description: '', quantity: 1, unit: 'Nos.', price: 0, gst_rate: 0, item_total: 0 };
}

export default function QuotationForm({ initialData, onSubmit, submitLabel = 'Create Quotation', saving = false, docType = 'quotation' }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [subject, setSubject] = useState('');
  const [quotedBy, setQuotedBy] = useState('');
  const [customer, setCustomer] = useState({ name: '', address: '', contact: '', email: '', gst_number: '' });
  const [billTo, setBillTo] = useState({ name: '', address: '', contact: '', gst_number: '' });
  const [shipTo, setShipTo] = useState({ name: '', address: '', contact: '', gst_number: '' });
  const [items, setItems] = useState([newItem()]);
  const [buybackItems, setBuybackItems] = useState([]);
  const [termsConditions, setTermsConditions] = useState(DEFAULT_TERMS);
  const [paymentPercentage, setPaymentPercentage] = useState(100);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [u, c, p, cust] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/companies`),
          axios.get(`${API}/products`),
          axios.get(`${API}/customers`)
        ]);
        setUsers(u.data);
        setCompanies(c.data);
        setProducts(p.data);
        setCustomers(cust.data);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  // Populate form when initialData arrives (for edit mode)
  useEffect(() => {
    if (initialData && users.length && companies.length) {
      const user = users.find(u => u.name === initialData.user_name);
      if (user) setSelectedUserId(user.id);
      setSelectedCompanyId(initialData.company_id || '');
      setSubject(initialData.subject || '');
      setQuotedBy(initialData.quoted_by || '');
      setCustomer(initialData.customer || { name: '', address: '', contact: '', email: '', gst_number: '' });
      if (initialData.bill_to) setBillTo(initialData.bill_to);
      if (initialData.ship_to) setShipTo(initialData.ship_to);
      const loadedItems = initialData.items && initialData.items.length > 0
        ? initialData.items.map(item => ({
            ...item,
            _key: genKey(),
            _featureKeys: (item.features || []).map(() => genKey()),
          }))
        : [newItem()];
      setItems(loadedItems);
      const loadedBuyback = initialData.buyback_items && initialData.buyback_items.length > 0
        ? initialData.buyback_items.map(bb => ({ ...bb, _key: genKey() }))
        : [];
      setBuybackItems(loadedBuyback);
      setTermsConditions(initialData.terms_conditions || DEFAULT_TERMS);
      if (initialData.payment_percentage !== undefined) setPaymentPercentage(initialData.payment_percentage);
    }
  }, [initialData, users, companies]);

  // Update GST number in terms when company changes (new quotations only)
  // Also populate Bill To with company details for new purchase orders
  useEffect(() => {
    if (initialData) return;
    const company = companies.find(c => c.id === selectedCompanyId);
    setTermsConditions(buildDefaultTerms(company?.gst_number));
    if (docType === 'purchase_order' && company) {
      setBillTo({
        name: company.name || '',
        address: company.address || '',
        contact: company.contact || '',
        gst_number: company.gst_number || '',
      });
    }
  }, [selectedCompanyId, companies, initialData, docType]);

  const handleProductChange = (idx, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    const features = [...(prod.features || [])];
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      product_id: prod.id,
      product_name: prod.name,
      description: '',
      features,
      _featureKeys: features.map(() => genKey()),
      warranty: prod.warranty || '',
      hsn_code: prod.hsn_code || '',
      price: prod.price,
      gst_rate: prod.gst_rate,
      item_total: prod.price * updated[idx].quantity
    };
    setItems(updated);
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'quantity' || field === 'price') {
      const qty = field === 'quantity' ? (parseInt(value) || 0) : updated[idx].quantity;
      const price = field === 'price' ? (parseFloat(value) || 0) : updated[idx].price;
      updated[idx].item_total = qty * price;
      if (field === 'quantity') updated[idx].quantity = qty;
      if (field === 'price') updated[idx].price = price;
    }
    setItems(updated);
  };

  const updateFeature = (itemIdx, featIdx, value) => {
    const updated = [...items];
    const features = [...updated[itemIdx].features];
    features[featIdx] = value;
    updated[itemIdx] = { ...updated[itemIdx], features };
    setItems(updated);
  };

  const addFeature = (itemIdx) => {
    const updated = [...items];
    updated[itemIdx] = {
      ...updated[itemIdx],
      features: [...updated[itemIdx].features, ''],
      _featureKeys: [...(updated[itemIdx]._featureKeys || []), genKey()],
    };
    setItems(updated);
  };

  const removeFeature = (itemIdx, featIdx) => {
    const updated = [...items];
    const features = [...updated[itemIdx].features];
    features.splice(featIdx, 1);
    const featureKeys = [...(updated[itemIdx]._featureKeys || [])];
    featureKeys.splice(featIdx, 1);
    updated[itemIdx] = { ...updated[itemIdx], features, _featureKeys: featureKeys };
    setItems(updated);
  };

  const addItem = () => setItems([...items, newItem()]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  // Buyback helpers
  const addBuybackItem = () => setBuybackItems([...buybackItems, newBuybackItem()]);
  const removeBuybackItem = (idx) => setBuybackItems(buybackItems.filter((_, i) => i !== idx));
  const updateBuybackItem = (idx, field, value) => {
    const updated = [...buybackItems];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'quantity' || field === 'price') {
      const qty = field === 'quantity' ? (parseInt(value) || 0) : updated[idx].quantity;
      const price = field === 'price' ? (parseFloat(value) || 0) : updated[idx].price;
      updated[idx].item_total = qty * price;
      if (field === 'quantity') updated[idx].quantity = qty;
      if (field === 'price') updated[idx].price = price;
    }
    if (field === 'gst_rate') updated[idx].gst_rate = parseFloat(value) || 0;
    setBuybackItems(updated);
  };

  const handleCustomerSelect = (customerId) => {
    if (!customerId) return;
    const c = customers.find(x => x.id === customerId);
    if (c) setCustomer({ name: c.name, address: c.address, contact: c.contact, email: c.email, gst_number: c.gst_number });
  };

  const subtotal = items.reduce((s, i) => s + i.item_total, 0);
  const gstTotal = items.reduce((s, i) => s + (i.item_total * i.gst_rate / 100), 0);
  const buybackSubtotal = buybackItems.reduce((s, i) => s + i.item_total, 0);
  const buybackGstTotal = buybackItems.reduce((s, i) => s + (i.item_total * i.gst_rate / 100), 0);
  const grandTotal = subtotal + gstTotal - buybackSubtotal - buybackGstTotal;

  const handleSubmit = (e) => {
    e.preventDefault();
    const user = users.find(u => u.id === selectedUserId);
    const company = companies.find(c => c.id === selectedCompanyId);
    if (!user) return alert('Please select a user');
    if (!company) return alert('Please select a company');
    if (items.some(i => !i.product_id)) return alert('Please select products for all items');

    const cleanItems = items.map(({ _key, _featureKeys, ...rest }) => rest);
    const cleanBuyback = buybackItems.map(({ _key, ...rest }) => rest);

    const payload = {
      user_name: user.name,
      user_phone: user.phone || '',
      quoted_by: quotedBy,
      company_id: company.id,
      company_name: company.name,
      subject,
      customer,
      items: cleanItems,
      buyback_items: cleanBuyback,
      subtotal,
      gst_total: gstTotal,
      buyback_subtotal: buybackSubtotal,
      buyback_gst_total: buybackGstTotal,
      grand_total: grandTotal,
      terms_conditions: termsConditions
    };
    if (docType === 'purchase_order') {
      payload.bill_to = billTo;
      payload.ship_to = shipTo;
    }
    if (docType === 'proforma_invoice') {
      payload.payment_percentage = parseFloat(paymentPercentage) || 100;
      payload.payment_amount = grandTotal * (parseFloat(paymentPercentage) || 100) / 100;
    }
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Meta: user + company */}
      <section className="bg-white border border-stone-200 p-6">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-4">Quotation Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Created By">
            <select data-testid="select-user" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} required className="input-field">
              <option value="">Select User</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Company">
            <select data-testid="select-company" value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} required className="input-field">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Subject">
            <input type="text" data-testid="quotation-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g., Quotation for the supply of..." className="input-field" />
          </Field>
          <Field label="Quoted By (internal only)">
            <input type="text" data-testid="quoted-by" value={quotedBy} onChange={e => setQuotedBy(e.target.value)} placeholder="Person who prepared this quote" className="input-field" />
          </Field>
        </div>
      </section>

      {/* Customer / Vendor */}
      <section className="bg-white border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">
            {docType === 'purchase_order' ? 'Vendor Details' : 'Customer Details'}
          </h2>
          {customers.length > 0 && (
            <div className="w-64">
              <SearchableSelect
                testId="select-saved-customer"
                options={customers.map(c => ({ value: c.id, label: c.name }))}
                value=""
                onChange={handleCustomerSelect}
                placeholder={docType === 'purchase_order' ? 'Load saved vendor...' : 'Load saved customer...'}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={docType === 'purchase_order' ? 'Vendor Name' : 'Customer Name'}>
            <input data-testid="customer-name" type="text" value={customer.name} onChange={e => setCustomer({...customer, name: e.target.value})} className="input-field" placeholder={docType === 'purchase_order' ? 'Vendor / Supplier name' : 'Customer / Company name'} />
          </Field>
          <Field label="Contact">
            <input data-testid="customer-contact" type="text" value={customer.contact} onChange={e => setCustomer({...customer, contact: e.target.value})} className="input-field" placeholder="Phone number" />
          </Field>
          <Field label="Email">
            <input data-testid="customer-email" type="email" value={customer.email} onChange={e => setCustomer({...customer, email: e.target.value})} className="input-field" placeholder="Email address" />
          </Field>
          <Field label={docType === 'purchase_order' ? 'Vendor GST No.' : 'Customer GST No.'}>
            <input data-testid="customer-gst" type="text" value={customer.gst_number} onChange={e => setCustomer({...customer, gst_number: e.target.value})} className="input-field" placeholder="GST Number (optional)" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Address">
            <textarea data-testid="customer-address" value={customer.address} onChange={e => setCustomer({...customer, address: e.target.value})} rows="2" className="input-field" placeholder="Full address" />
          </Field>
        </div>
      </section>

      {/* Bill To / Ship To (Purchase Order only) */}
      {docType === 'purchase_order' && (
        <section className="bg-white border border-stone-200 p-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-4">Billing & Shipping</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-bold text-stone-600 mb-3 border-b border-stone-200 pb-1">Bill To</h3>
              <div className="space-y-3">
                <Field label="Name"><input data-testid="bill-to-name" type="text" value={billTo.name} onChange={e => setBillTo({...billTo, name: e.target.value})} className="input-field" placeholder="Billing name" /></Field>
                <Field label="Address"><textarea data-testid="bill-to-address" value={billTo.address} onChange={e => setBillTo({...billTo, address: e.target.value})} rows="2" className="input-field" placeholder="Billing address" /></Field>
                <Field label="Contact"><input data-testid="bill-to-contact" type="text" value={billTo.contact} onChange={e => setBillTo({...billTo, contact: e.target.value})} className="input-field" placeholder="Contact number" /></Field>
                <Field label="GST No."><input data-testid="bill-to-gst" type="text" value={billTo.gst_number} onChange={e => setBillTo({...billTo, gst_number: e.target.value})} className="input-field" placeholder="GST number" /></Field>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-stone-600 mb-3 border-b border-stone-200 pb-1">Ship To</h3>
              <div className="space-y-3">
                <Field label="Name"><input data-testid="ship-to-name" type="text" value={shipTo.name} onChange={e => setShipTo({...shipTo, name: e.target.value})} className="input-field" placeholder="Shipping name" /></Field>
                <Field label="Address"><textarea data-testid="ship-to-address" value={shipTo.address} onChange={e => setShipTo({...shipTo, address: e.target.value})} rows="2" className="input-field" placeholder="Shipping address" /></Field>
                <Field label="Contact"><input data-testid="ship-to-contact" type="text" value={shipTo.contact} onChange={e => setShipTo({...shipTo, contact: e.target.value})} className="input-field" placeholder="Contact number" /></Field>
                <Field label="GST No."><input data-testid="ship-to-gst" type="text" value={shipTo.gst_number} onChange={e => setShipTo({...shipTo, gst_number: e.target.value})} className="input-field" placeholder="GST number" /></Field>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Items */}
      <section className="bg-white border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">Line Items</h2>
          <button type="button" onClick={addItem} data-testid="add-item-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:bg-[#002FA7]/5 px-3 py-1.5 transition-colors">
            <Plus size={14} weight="bold" /> Add Item
          </button>
        </div>

        <div className="space-y-0">
          {items.map((item, idx) => (
            <div key={item._key} className="border border-stone-200 mb-4" data-testid={`item-block-${idx}`}>
              <div className="bg-stone-50 px-4 py-3 flex items-center justify-between border-b border-stone-200">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Item {idx + 1}</span>
                <button type="button" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} disabled={items.length === 1}
                  className="p-1 text-stone-400 hover:text-red-600 disabled:opacity-20 transition-colors">
                  <X size={16} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-5">
                    <label className="lbl">Product</label>
                    <SearchableSelect
                      testId={`select-product-${idx}`}
                      options={products.map(p => ({ value: p.id, label: p.name }))}
                      value={item.product_id}
                      onChange={val => handleProductChange(idx, val)}
                      placeholder="Search product..."
                      required
                    />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">Qty</label>
                    <input type="number" data-testid={`item-quantity-${idx}`} value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} min="1" className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">Unit</label>
                    <input type="text" value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="lbl">Rate (Rs.)</label>
                    <input type="number" step="0.01" data-testid={`item-price-${idx}`} value={item.price} onChange={e => updateItem(idx, 'price', e.target.value)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">GST %</label>
                    <input type="number" step="0.01" data-testid={`item-gst-${idx}`} value={item.gst_rate} onChange={e => updateItem(idx, 'gst_rate', parseFloat(e.target.value) || 0)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="lbl">Amount</label>
                    <div className="input-field bg-stone-100 text-stone-900 font-semibold" data-testid={`item-total-${idx}`}>
                      Rs. {item.item_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="lbl">Additional Description</label>
                  <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="input-field" placeholder="Model details, warranty info..." />
                </div>

                <div className="w-48">
                  <label className="lbl">HSN Code</label>
                  <input type="text" value={item.hsn_code} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} className="input-field" placeholder="HSN code" />
                </div>

                {item.product_id && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="lbl mb-0">Features / Specifications</label>
                      <button type="button" onClick={() => addFeature(idx)} className="text-xs text-[#002FA7] font-semibold hover:underline">+ Add Feature</button>
                    </div>
                    {item.features.length > 0 ? (
                      <div className="space-y-1.5 pl-2 border-l-2 border-stone-200">
                        {item.features.map((feat, fi) => (
                          <div key={item._featureKeys?.[fi] || `fb-${fi}`} className="flex items-center gap-2">
                            <span className="text-stone-400 text-xs">&#8226;</span>
                            <input type="text" value={feat} onChange={e => updateFeature(idx, fi, e.target.value)}
                              className="flex-1 bg-transparent border-b border-stone-200 focus:border-[#002FA7] text-sm py-1 px-0 outline-none transition-colors"
                              placeholder="Feature description..." />
                            <button type="button" onClick={() => removeFeature(idx, fi)} className="text-stone-300 hover:text-red-500 transition-colors">
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-stone-400 italic">No features. Click &quot;+ Add Feature&quot; to add.</p>
                    )}

                      <div className="mt-4">
                        <label className="lbl">Warranty</label>
                        <input type="text" value={item.warranty || ''} onChange={e => updateItem(idx, 'warranty', e.target.value)}
                          className="input-field" placeholder="e.g., 2 Years onsite warranty" />
                      </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={addItem} data-testid={`add-item-after-${idx}`}
                className="w-full py-2 border-t border-dashed border-stone-200 text-xs font-semibold text-stone-400 hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors flex items-center justify-center gap-1.5">
                <Plus size={12} weight="bold" /> Add Item
              </button>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="border-t border-stone-200 pt-5 mt-2">
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Subtotal (excl. GST)</span>
                <span className="font-semibold text-stone-800" data-testid="subtotal-display">Rs. {subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">GST Total</span>
                <span className="font-semibold text-stone-800" data-testid="gst-total-display">Rs. {gstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              {buybackItems.length > 0 && (
                <>
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Less: Buyback</span>
                    <span className="font-semibold" data-testid="buyback-subtotal-display">- Rs. {buybackSubtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {buybackGstTotal > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Less: Buyback GST</span>
                      <span className="font-semibold" data-testid="buyback-gst-display">- Rs. {buybackGstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between text-base pt-2 border-t border-stone-900">
                <span className="font-bold text-stone-900">Net Payable</span>
                <span className="font-bold text-stone-900" data-testid="grand-total-display">Rs. {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buyback Section */}
      <section className="bg-white border border-red-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-red-500">Buyback Items</h2>
            <p className="text-[11px] text-stone-400 mt-0.5">Items bought back from customer — deducted from total</p>
          </div>
          <button type="button" onClick={addBuybackItem} data-testid="add-buyback-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 px-3 py-1.5 transition-colors">
            <Plus size={14} weight="bold" /> Add Buyback
          </button>
        </div>

        {buybackItems.length === 0 ? (
          <p className="text-xs text-stone-400 italic py-4 text-center">No buyback items. Click &quot;+ Add Buyback&quot; to add.</p>
        ) : (
          <div className="space-y-3">
            {buybackItems.map((bb, idx) => (
              <div key={bb._key} className="border border-red-100 bg-red-50/30 p-4" data-testid={`buyback-block-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-red-400">Buyback {idx + 1}</span>
                  <button type="button" onClick={() => removeBuybackItem(idx)} data-testid={`remove-buyback-${idx}`}
                    className="p-1 text-stone-400 hover:text-red-600 transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 md:col-span-5">
                    <label className="lbl">Product / Item Name</label>
                    <input type="text" data-testid={`buyback-name-${idx}`} value={bb.product_name}
                      onChange={e => updateBuybackItem(idx, 'product_name', e.target.value)}
                      className="input-field" placeholder="e.g., Old UPS System" required />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">Qty</label>
                    <input type="number" data-testid={`buyback-qty-${idx}`} value={bb.quantity}
                      onChange={e => updateBuybackItem(idx, 'quantity', e.target.value)} min="1" className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">Unit</label>
                    <input type="text" value={bb.unit} onChange={e => updateBuybackItem(idx, 'unit', e.target.value)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="lbl">Buyback Rate</label>
                    <input type="number" step="0.01" data-testid={`buyback-price-${idx}`} value={bb.price}
                      onChange={e => updateBuybackItem(idx, 'price', e.target.value)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-1">
                    <label className="lbl">GST %</label>
                    <input type="number" step="0.01" data-testid={`buyback-gst-${idx}`} value={bb.gst_rate}
                      onChange={e => updateBuybackItem(idx, 'gst_rate', e.target.value)} className="input-field" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="lbl">Amount</label>
                    <div className="input-field bg-red-50 text-red-700 font-semibold" data-testid={`buyback-total-${idx}`}>
                      Rs. {bb.item_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="lbl">Description (optional)</label>
                  <input type="text" value={bb.description} onChange={e => updateBuybackItem(idx, 'description', e.target.value)}
                    className="input-field" placeholder="Condition, serial number, etc." />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Payment Percentage (Proforma Invoice only) */}
      {docType === 'proforma_invoice' && (
        <section className="bg-white border border-[#002FA7]/20 p-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7] mb-4">Payment Terms</h2>
          <div className="flex items-end gap-4">
            <div className="w-40">
              <label className="lbl">Payment %</label>
              <input type="number" data-testid="payment-percentage" min="1" max="100" step="1"
                value={paymentPercentage} onChange={e => setPaymentPercentage(e.target.value)}
                className="input-field text-center text-lg font-bold" />
            </div>
            <div className="flex-1 bg-[#002FA7]/5 border border-[#002FA7]/10 p-4">
              <p className="text-xs text-stone-500 mb-1">Amount Payable ({paymentPercentage}% of Rs. {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })})</p>
              <p className="text-xl font-bold text-[#002FA7]" data-testid="payment-amount">
                Rs. {(grandTotal * (parseFloat(paymentPercentage) || 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* T&C */}
      <section className="bg-white border border-stone-200 p-6">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-4">Terms & Conditions</h2>
        <textarea data-testid="terms-conditions-input" value={termsConditions} onChange={e => setTermsConditions(e.target.value)} rows="7" className="input-field text-sm" />
      </section>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button type="submit" data-testid="save-quotation-button" disabled={saving}
          className="px-6 py-2.5 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all active:scale-[0.97] disabled:opacity-50">
          {saving ? 'Saving...' : submitLabel}
        </button>
      </div>

      <style>{`
        .input-field {
          width: 100%;
          background: #fafaf9;
          border: 1px solid #e7e5e4;
          padding: 8px 12px;
          font-size: 13px;
          color: #1c1917;
          transition: all 0.15s;
          outline: none;
        }
        .input-field:focus {
          border-color: #002FA7;
          box-shadow: 0 0 0 1px #002FA7;
        }
        .lbl {
          display: block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #78716c;
          margin-bottom: 6px;
        }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="lbl">{label}</label>
      {children}
    </div>
  );
}
